import { useState, useContext, useEffect, useCallback } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import NewVersionBanner from './NewVersionBanner';
import { ThemeProvider } from './ThemeProvider';
import { cn } from '../../lib/utils';
import ContextStore from '../../hooks/ContextStore';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';
import { urls } from '../../dispatch/urls';
import UiPaths from '../../paths/uiPaths';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { contextStore, setContextStore } = useContext(ContextStore) as any;
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  );
  const [stores, setStores] = useState<any[]>([]);

  const toggleDesktopSidebar = useCallback(() => {
    setSidebarCollapsed(c => {
      localStorage.setItem('sidebar-collapsed', String(!c));
      return !c;
    });
  }, []);

  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getStores, {}, contextStore?.token);
      if (resp && !('error' in resp) && Array.isArray(resp)) setStores(resp);
    })();
  }, [contextStore?.token]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // 🩺 health watch (owner 2026-10-30): problems shown in the app, not on Telegram — red bar for admins
  const isAdmin = contextStore?.user?.type === 'admin';
  const [health, setHealth] = useState<{ label: string; detail: string }[]>([]);
  useEffect(() => {
    if (!isAdmin || !contextStore?.token) return;
    let alive = true;
    const pull = async () => {
      try {
        const r = await fetch(`${urls.baseUrlRestApi}/health-watch`, { headers: { 'x-auth-token': contextStore.token } });
        const rows = await r.json();
        if (alive && Array.isArray(rows)) setHealth(rows.filter((x: any) => x.ok === false));
      } catch { /* bar stays as it was */ }
    };
    pull();
    const t = setInterval(pull, 5 * 60 * 1000);
    window.addEventListener('health-changed', pull);
    return () => { alive = false; clearInterval(t); window.removeEventListener('health-changed', pull); };
  }, [isAdmin, contextStore?.token]);

  const handleStoreChange = useCallback((storeId: string) => {
    if (!setContextStore) return;
    if (storeId === '0') {
      setContextStore((prev: any) => ({ ...prev, store: {} }));
    } else {
      const found = stores.find(s => s._id === storeId);
      if (found) setContextStore((prev: any) => ({ ...prev, store: found }));
    }
  }, [stores, setContextStore]);

  return (
    <ThemeProvider>
      <NewVersionBanner />
      <div className="flex h-screen overflow-hidden bg-background text-foreground">

        {/* ── Sidebar (always fixed — spacer below provides layout gap) ─── */}
        <Sidebar
          open={sidebarOpen}
          collapsed={sidebarCollapsed}
          onClose={() => setSidebarOpen(false)}
          onToggle={toggleDesktopSidebar}
        />

        {/* ── Desktop spacer: slides from w-60 → w-0 when sidebar collapses ── */}
        <div
          className={cn(
            'hidden lg:flex shrink-0 transition-[width] duration-200 ease-in-out',
            sidebarCollapsed ? 'w-0' : 'w-60'
          )}
          aria-hidden="true"
        />

        {/* ── Main content area ─────────────────────────── */}
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
          <Topbar
            onMenuClick={() => setSidebarOpen(true)}
            onSidebarToggle={toggleDesktopSidebar}
            sidebarCollapsed={sidebarCollapsed}
            stores={stores}
            onStoreChange={handleStoreChange}
          />
          {isAdmin && health.length > 0 && window.location.pathname !== UiPaths.SystemHealth && (
            <a href={UiPaths.SystemHealth} style={{ display: 'block', background: '#b91c1c', color: '#fff', fontSize: 13, fontWeight: 700, padding: '6px 14px', textDecoration: 'none' }}>
              🩺 {health.length} problem{health.length > 1 ? 's' : ''}: {health.map((h) => h.label).join(' · ')} — open System Health →
            </a>
          )}
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}
